// src/tenantAdmin/companyApi.js
//
// The company (customer-admin) routes a NON-superadmin may call. ⛔ Call them only
// after `isCustomerAdminOf(cid)`: an unknown or foreign company id answers 403
// CUSTOMER_ADMIN_REQUIRED (never 404), which also raises the global drift banner.
// (A malformed, non-UUID id is a 422 — ids only ever come from the login claims.)
//
// ⚠️ They live under `/api/v1/admin/*`, which is otherwise superadmin-only; these
// `customers/{cid}…` routes are the documented exception. The gate runs BEFORE the
// lookup, so the 403 cannot be read as "no such company" and must never be worded
// as "not found".
//
// ⛔ `suppressTenantHeader: true` on every call: these routes are scoped by the
// company id in the path, and an X-Tenant-Id riding along says nothing about which
// company is meant.
//
// The workspace-level half of this console lives in ./tenantAdminApi.js.
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';

// The billing record itself stays where the superadmin console already reads it
// (src/admin/adminApi.js, also used by TenantCreditsSection). Re-exported under
// the names this console uses so a page needs ONE import, never two.
// companyApi.test.js asserts the identity, so a tidy-up cannot move them.
export { adminGetCustomer as getCompany, adminUpdateCustomer as updateCompany } from '../admin/adminApi.js';

// A missing company id is a PROGRAMMING error (the caller skipped the claims
// lookup), so it throws before anything reaches the network — `/customers/undefined`
// would otherwise come back as a 403 indistinguishable from a real refusal.
const base = (cid) => {
  if (!cid) throw new TypeError('companyApi: a customer id is required');
  return `/api/v1/admin/customers/${encodeURIComponent(cid)}`;
};

// Mirrors `readAdminError` in src/admin/adminApi.js: FastAPI wraps a raised
// HTTPException detail in `{ detail: {...} }` while the handled envelope arrives
// flat, so unwrap `detail` when it is an object and take the body as-is otherwise.
// buildApiError then carries err.status, err.code and the flat top-level extras
// (`used`/`limit`/`source` of a SEAT_LIMIT_REACHED, `retry_after_seconds` of a 429,
// `previous_email` of an email correction) — which is what lets every caller branch
// on the code and word the refusal itself. ⛔ Never render err.message — and,
// unlike adminApi.js, never overwrite it with localised copy either: refusals are
// worded at render time from `err.code`, so a pre-baked sentence would be a second
// source of truth.
async function call(path, init = {}, fallback = 'Request failed') {
  const res = await apiFetch(path, { ...init, suppressTenantHeader: true });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const envelope = body && typeof body.detail === 'object' && body.detail !== null ? body.detail : body;
    throw buildApiError(envelope, res.status, { fallbackMessage: `${fallback}: ${res.status}` });
  }
  if (res.status === 204) return null;
  return res.json().catch(() => null);
}

// ── Workspaces of the company ──────────────────────────────────────
//
// `include_inactive=true` by default: a retired workspace still occupies a
// workspace seat, so a list that hid them would make `seats_used` look wrong.
// The envelope carries `seats_used` / `seat_limit` / `seat_source` (workspaces,
// NOT people — the per-person cap is `user_seats_*` on the workspace routes).
export const listCompanyWorkspaces = (cid, { includeInactive = true } = {}) =>
  call(`${base(cid)}/tenants?include_inactive=${includeInactive ? 'true' : 'false'}`, {}, 'Failed to list workspaces');

// 201 answers `relogin_required: true`. The new workspace is NOT in the caller's
// `tenants[]` claim — nothing on `dev` re-mints it — so it is unusable until the
// next sign-in, and the caller must say so rather than navigating to it.
export const createCompanyWorkspace = (cid, displayName) =>
  call(`${base(cid)}/tenants`, { method: 'POST', body: JSON.stringify({ display_name: displayName }) }, 'Failed to create workspace');

// ── Company members (admin | viewer) ───────────────────────────────
//
// These are the company-level rows: an `admin` administers every workspace the
// company owns (an implicit workspace claim, no membership row), a `viewer` only
// reads the company's consumption. They are the same people the workspace list
// appends with `via: "customer"` and refuses to manage.
export const listCompanyMembers = (cid) => call(`${base(cid)}/users`, {}, 'Failed to list members');

// TWO successful outcomes, distinguished by `status`, never by the HTTP code
// alone: 201 `attached` (the address already had an account) or 202
// `invitation_sent` (an invitation went out, and the row appears only once it is
// accepted). `email_sent: false` on the 202 is NOT a success for the user.
export const addCompanyMember = (cid, { email, role = 'viewer' }) =>
  call(`${base(cid)}/users`, { method: 'POST', body: JSON.stringify({ email, role }) }, 'Failed to add member');

// 409 CANNOT_REMOVE_LAST_CUSTOMER_ADMIN / CANNOT_DEMOTE_SELF: both are refusals of
// the LAST line of administration, not transient failures — never auto-retried.
export const changeCompanyMemberRole = (cid, uid, role) =>
  call(`${base(cid)}/users/${uid}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }, 'Failed to change role');

// 204, so `call` answers null: there is no body to read.
export const removeCompanyMember = (cid, uid) =>
  call(`${base(cid)}/users/${uid}`, { method: 'DELETE' }, 'Failed to remove member');

// Corrects a typo in the address someone signs in with. It sends NO email, so the
// response's `previous_email` → `email` pair is the only thing that can tell the
// admin what to pass on out of band.
export const correctCompanyMemberEmail = (cid, uid, email) =>
  call(`${base(cid)}/users/${uid}/email`, { method: 'PATCH', body: JSON.stringify({ email }) }, 'Failed to correct email');
